import React, { useEffect, useState } from 'react';
import { db, auth, dbRealtime, createUserWithEmailAndPassword } from '../firebase';
import { collection, getDocs, setDoc, doc, updateDoc, deleteDoc, onSnapshot, getDoc } from 'firebase/firestore';
import { ref, get as dbGet, set as dbSet, remove as dbRemove } from 'firebase/database';
import { updatePassword, deleteUser } from 'firebase/auth';
import { encodeEmail } from '../utils/helpers';

const USERS_COLLECTION = 'users';
const APPLICATIONS_COLLECTION = 'applications';

export default function AdminPage() {
  const [pending, setPending] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [updatingUserId, setUpdatingUserId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [usageStats, setUsageStats] = useState({ today: 0, total: 0 });
  const [userUsageData, setUserUsageData] = useState({});
  const [showAddUser, setShowAddUser] = useState(false);
  const [showEditUser, setShowEditUser] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [newUser, setNewUser] = useState({ email: '', name: '', password: '' });
  const [editUser, setEditUser] = useState({ name: '', password: '' });
  const [creatingUser, setCreatingUser] = useState(false);
  const [updatingUser, setUpdatingUser] = useState(false);
  const [editingNameEmail, setEditingNameEmail] = useState(null);
  const [editingNameValue, setEditingNameValue] = useState('');



  // Function to remove users without permission
  const removeUsersWithoutPermission = async (userList) => {
    const usersToRemove = userList.filter(user => !user.allow);

    for (const user of usersToRemove) {
      try {
        // Delete from Firestore
        await deleteDoc(doc(db, USERS_COLLECTION, user.email));

        // Remove from Realtime Database usage data
        const userUsageRef = ref(dbRealtime, `usage/users/${encodeEmail(user.email)}`);
        await dbRemove(userUsageRef);

        console.log(`Removed user without permission: ${user.email}`);
      } catch (error) {
        console.error(`Error removing user ${user.email}:`, error);
      }
    }

    if (usersToRemove.length > 0) {
      setMessage(`Automatically removed ${usersToRemove.length} user(s) without permission`);
    }
  };

  useEffect(() => {
    // Listen for pending applications
    const unsubApps = onSnapshot(collection(db, APPLICATIONS_COLLECTION), (snap) => {
      setPending(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });
    // Listen for users
    const unsubUsers = onSnapshot(collection(db, USERS_COLLECTION), (snap) => {
      const userData = snap.docs.map(doc => ({
        id: doc.id, // Document ID should be the email
        ...doc.data()
      }));
      setUsers(userData);
      setLoading(false);
    });

    // Fetch usage statistics
    const fetchUsageStats = async () => {
      try {
        const today = new Date().toISOString().split('T')[0];
        const dailyRef = ref(dbRealtime, `usage/daily/${today}`);
        const totalRef = ref(dbRealtime, 'usage/total');

        const [dailySnapshot, totalSnapshot] = await Promise.all([
          dbGet(dailyRef),
          dbGet(totalRef)
        ]);

        setUsageStats({
          today: dailySnapshot.exists() ? dailySnapshot.val() : 0,
          total: totalSnapshot.exists() ? totalSnapshot.val() : 0
        });
      } catch (error) {
        console.error('Error fetching usage stats:', error);
      }
    };

    fetchUsageStats();
    const interval = setInterval(() => {
      fetchUsageStats();
    }, 30000); // Refresh every 30 seconds

    return () => {
      unsubApps();
      unsubUsers();
      clearInterval(interval);
    };
  }, []); // Remove users dependency

  // Separate useEffect for fetching user usage when users change
  useEffect(() => {
    const fetchUserUsage = async () => {
      if (users.length === 0) return;

      try {
        const today = new Date().toISOString().split('T')[0];
        const userUsagePromises = users.map(async (user) => {
          const userDailyRef = ref(dbRealtime, `usage/users/${encodeEmail(user.email)}/daily/${today}`);
          const userTotalRef = ref(dbRealtime, `usage/users/${encodeEmail(user.email)}/total`);

          const [userDailySnapshot, userTotalSnapshot] = await Promise.all([
            dbGet(userDailyRef),
            dbGet(userTotalRef)
          ]);

          const dailyValue = userDailySnapshot.exists() ? userDailySnapshot.val() : 0;
          const totalValue = userTotalSnapshot.exists() ? userTotalSnapshot.val() : 0;

          return {
            email: user.email,
            today: dailyValue,
            total: totalValue
          };
        });

        const userUsageResults = await Promise.all(userUsagePromises);
        const userUsageMap = {};
        userUsageResults.forEach(result => {
          userUsageMap[encodeEmail(result.email)] = { today: result.today, total: result.total };
        });

        setUserUsageData(userUsageMap);
      } catch (error) {
        console.error('Error fetching user usage:', error);
      }
    };

    fetchUserUsage();
    const interval = setInterval(() => {
      fetchUserUsage();
    }, 30000); // Refresh every 30 seconds

    return () => clearInterval(interval);
  }, [users]); // Only depend on users

  const formatLastLogin = (lastLogin) => {
    if (!lastLogin) return 'Never';
    try {
      const date = new Date(lastLogin);
      const now = new Date();
      const diffMs = now - date;
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);

      if (diffMins < 1) return 'Just now';
      if (diffMins < 60) return `${diffMins}m ago`;
      if (diffHours < 24) return `${diffHours}h ago`;
      if (diffDays < 7) return `${diffDays}d ago`;

      return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } catch (error) {
      return 'Invalid date';
    }
  };

  const approveUser = async (app) => {
    await setDoc(doc(db, USERS_COLLECTION, app.email), {
      email: app.email,
      allow: true,
      admin: false,
      name: app.name || '',
    });
    await deleteDoc(doc(db, APPLICATIONS_COLLECTION, app.id));
    setMessage(`Approved ${app.email}`);
  };

  const rejectUser = async (app) => {
    await deleteDoc(doc(db, APPLICATIONS_COLLECTION, app.id));
    setMessage(`Rejected ${app.email}`);
  };

  const setAllow = async (user, allow) => {
    if (!allow) {
      // If disallowing, remove the user from the database
      try {
        // Note: deleteUser requires admin SDK or the user to be currently signed in
        // For now, we just delete from Firestore
        await deleteDoc(doc(db, USERS_COLLECTION, user.email));

        // Also remove from Realtime Database usage data
        const userUsageRef = ref(dbRealtime, `usage/users/${encodeEmail(user.email)}`);
        await dbRemove(userUsageRef);

        setMessage(`Removed ${user.email} from database`);
      } catch (error) {
        console.error('Error removing user:', error);
        setMessage(`Failed to remove ${user.email}: ${error.message}`);
      }
    } else {
      // If allowing, update the user
      await updateDoc(doc(db, USERS_COLLECTION, user.email), { allow });
      setMessage(`Allowed ${user.email}`);
    }
  };

  const setAdmin = async (user, admin) => {
    try {
      // Update Firestore
      await updateDoc(doc(db, USERS_COLLECTION, user.email), { admin });

      // Get user's UID from Firestore if not available
      let userUID = user.uid;
      if (!userUID) {
        const userDoc = await getDoc(doc(db, USERS_COLLECTION, user.email));
        if (userDoc.exists()) {
          userUID = userDoc.data().uid;
        }
      }

      // Sync to Realtime Database admins node if UID is available
      if (userUID) {
        try {
          if (admin) {
            await dbSet(ref(dbRealtime, `admins/${userUID}`), true);
          } else {
            await dbRemove(ref(dbRealtime, `admins/${userUID}`));
          }
        } catch (error) {
          // If permission denied, the sync will happen when user logs in
          if (error.code !== 'PERMISSION_DENIED') {
            throw error;
          }
        }
      }

      setMessage(`${admin ? 'Granted admin to' : 'Revoked admin from'} ${user.email}. ${!userUID ? 'UID will be synced when user logs in.' : ''}`);
    } catch (error) {
      console.error('Error setting admin:', error);
      setMessage(`Failed to ${admin ? 'grant' : 'revoke'} admin: ${error.message}`);
    }
  };

  const createNewUser = async () => {
    if (!newUser.email || !newUser.email.includes('@')) {
      setMessage('Please enter a valid email address');
      return;
    }
    if (!newUser.name.trim()) {
      setMessage('Please enter a name');
      return;
    }
    if (!newUser.password || newUser.password.length < 6) {
      setMessage('Password must be at least 6 characters long');
      return;
    }

    setCreatingUser(true);
    try {
      // Check if user already exists
      const existingUser = users.find(u => u.email === newUser.email);
      if (existingUser) {
        setMessage('User with this email already exists');
        setCreatingUser(false);
        return;
      }

      // Create user in Firebase Auth and get UID
      const userCredential = await createUserWithEmailAndPassword(auth, newUser.email, newUser.password);
      const newUserUID = userCredential.user.uid;

      // Add user to Firestore with UID
      await setDoc(doc(db, USERS_COLLECTION, newUser.email), {
        email: newUser.email,
        name: newUser.name.trim(),
        allow: true,
        admin: false,
        uid: newUserUID,
        createdAt: new Date().toISOString(),
      });

      setMessage(`Successfully created user: ${newUser.email}`);
      setNewUser({ email: '', name: '', password: '' });
      setShowAddUser(false);
    } catch (error) {
      console.error('Error creating user:', error);
      setMessage(`Failed to create user: ${error.message}`);
    }
    setCreatingUser(false);
  };

  const updateUserInfo = async () => {
    if (!editUser.name.trim()) {
      setMessage('Please enter a name');
      return;
    }

    setUpdatingUser(true);
    try {
      const updates = { name: editUser.name.trim() };

      // Update password if provided
      if (editUser.password && editUser.password.length >= 6) {
        const user = auth.currentUser;
        if (user && user.email === editingUser.email) {
          await updatePassword(user, editUser.password);
        }
      }

      // Update user info in Firestore
      await updateDoc(doc(db, USERS_COLLECTION, editingUser.email), updates);

      setMessage(`Successfully updated user: ${editingUser.email}`);
      setEditUser({ name: '', password: '' });
      setShowEditUser(false);
      setEditingUser(null);
    } catch (error) {
      console.error('Error updating user:', error);
      setMessage(`Failed to update user: ${error.message}`);
    }
    setUpdatingUser(false);
  };

  const openEditUser = (user) => {
    setEditingUser(user);
    setEditUser({ name: user.name || '', password: '' });
    setShowEditUser(true);
  };

  const saveInlineName = async (email) => {
    const trimmed = editingNameValue.trim();
    try {
      await updateDoc(doc(db, USERS_COLLECTION, email), { name: trimmed });
      setMessage(`Name updated for ${email}`);
      setTimeout(() => setMessage(''), 2000);
    } catch (error) {
      setMessage(`Failed to update name: ${error.message}`);
    }
    setEditingNameEmail(null);
  };

  // Filter users: only show those with permissions and match search
  const filteredUsers = users.filter(user => {
    // Only show users with permission (allow is true)
    if (!user.allow) {
      return false;
    }

    // Filter by search term
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      const emailMatch = user.email && user.email.toLowerCase().includes(search);

      // Handle name search - check if any part of search matches any part of name
      let nameMatch = false;
      if (user.name) {
        const nameLower = user.name.toLowerCase();
        const searchParts = search.split(/\s+/).filter(part => part.length > 0);

        if (searchParts.length === 0) {
          nameMatch = nameLower.includes(search);
        } else {
          // Check if all search parts are found in the name
          nameMatch = searchParts.every(part => nameLower.includes(part));
        }
      }

      return emailMatch || nameMatch;
    }

    return true;
  });

  return (
    <div className="container">
      <div className="page-header">
        <h2>Admin Panel</h2>
        <p>Approve new users and manage permissions</p>
      </div>

      {/* Usage Statistics */}
      <div className="stats-card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
          <div style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap' }}>
            <div className="usage-stat">
              <div className="usage-stat__value">{usageStats.today}</div>
              <div className="usage-stat__label">Descriptions Today</div>
            </div>
            <div className="usage-stat">
              <div className="usage-stat__value">{usageStats.total}</div>
              <div className="usage-stat__label">Total Descriptions</div>
            </div>
          </div>
          <div style={{ fontSize: '0.9rem', opacity: 0.9 }}>
            Classifier Usage Statistics
          </div>
        </div>
      </div>

      {message && <div className="alert alert-success">{message}</div>}
      {/* Pending Applications */}
      <div className="card" style={{ marginBottom: '2rem' }}>
        <h3 style={{ color: 'var(--color-primary)', fontWeight: 600, marginBottom: '1rem' }}>Pending Applications</h3>
        {pending.length === 0 ? <p style={{ color: 'var(--color-text-muted)', fontStyle: 'italic' }}>No pending applications.</p> : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid var(--color-primary)', textAlign: 'left' }}>
                <th style={{ padding: '0.75rem 0.5rem', color: 'var(--color-primary)' }}>Email</th>
                <th style={{ padding: '0.75rem 0.5rem', color: 'var(--color-primary)' }}>Name</th>
                <th style={{ padding: '0.75rem 0.5rem', color: 'var(--color-primary)' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {pending.map(app => (
                <tr key={app.id} style={{ borderBottom: '1px solid var(--color-border)' }}>
                  <td style={{ padding: '0.75rem 0.5rem' }}>{app.email}</td>
                  <td style={{ padding: '0.75rem 0.5rem' }}>{app.name || ''}</td>
                  <td style={{ padding: '0.75rem 0.5rem' }}>
                    <button className="btn btn-primary" style={{ marginRight: '0.5rem', padding: '0.4rem 1rem', fontSize: '0.85rem' }} onClick={() => approveUser(app)}>Approve</button>
                    <button className="btn btn-secondary" style={{ padding: '0.4rem 1rem', fontSize: '0.85rem', color: 'var(--color-danger)' }} onClick={() => rejectUser(app)}>Reject</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* All Users */}
      <div className="card">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', flexWrap: 'wrap', gap: '0.5rem' }}>
          <h3 style={{ color: 'var(--color-primary)', fontWeight: 600 }}>All Users</h3>
          <button className="btn btn-primary" onClick={() => setShowAddUser(true)} style={{ fontSize: '0.85rem' }}>+ Add User</button>
        </div>
        <div style={{ marginBottom: '1rem' }}>
          <input
            type="text"
            placeholder="Search by email or name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="form-control"
          />
        </div>
        {loading ? <p style={{ color: 'var(--color-text-muted)', fontStyle: 'italic' }}>Loading...</p> : filteredUsers.length === 0 ? (
          <p style={{ color: 'var(--color-text-muted)', fontStyle: 'italic' }}>
            {searchTerm ? 'No users found matching your search.' : 'No users found.'}
          </p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.9rem' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid var(--color-primary)', textAlign: 'left' }}>
                  <th style={{ padding: '0.75rem 0.5rem', color: 'var(--color-primary)' }}>Email</th>
                  <th style={{ padding: '0.75rem 0.5rem', color: 'var(--color-primary)' }}>Name</th>
                  <th style={{ padding: '0.75rem 0.5rem', color: 'var(--color-primary)' }}>Last Login</th>
                  <th style={{ padding: '0.75rem 0.5rem', color: 'var(--color-primary)', textAlign: 'center' }}>Today</th>
                  <th style={{ padding: '0.75rem 0.5rem', color: 'var(--color-primary)', textAlign: 'center' }}>Total</th>
                  <th style={{ padding: '0.75rem 0.5rem', color: 'var(--color-primary)' }}>Allow</th>
                  <th style={{ padding: '0.75rem 0.5rem', color: 'var(--color-primary)' }}>Admin</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map(user => {
                  return (
                    <tr key={user.email} style={{ borderBottom: '1px solid var(--color-border)' }}>
                      <td style={{ padding: '0.75rem 0.5rem' }}>{user.email}</td>
                      <td style={{ padding: '0.75rem 0.5rem' }}>
                        {editingNameEmail === user.email ? (
                          <input
                            className="form-control"
                            type="text"
                            value={editingNameValue}
                            onChange={e => setEditingNameValue(e.target.value)}
                            onKeyDown={e => { if (e.key === 'Enter') saveInlineName(user.email); if (e.key === 'Escape') setEditingNameEmail(null); }}
                            onBlur={() => saveInlineName(user.email)}
                            autoFocus
                            style={{ padding: '0.3rem 0.5rem', fontSize: '0.9rem' }}
                          />
                        ) : (
                          <span
                            onClick={() => { setEditingNameEmail(user.email); setEditingNameValue(user.name || ''); }}
                            style={{ cursor: 'pointer', borderBottom: '1px dashed var(--color-border)', paddingBottom: '1px' }}
                            title="Click to edit name"
                          >
                            {user.name || <em style={{ color: 'var(--color-text-muted)' }}>No name</em>}
                          </span>
                        )}
                      </td>
                      <td style={{ padding: '0.75rem 0.5rem', fontSize: '0.85rem', color: 'var(--color-text-muted)' }}>
                        {formatLastLogin(user.lastLogin)}
                      </td>
                      <td style={{ padding: '0.75rem 0.5rem', textAlign: 'center', fontWeight: 600, color: 'var(--color-primary)' }}>
                        {userUsageData[encodeEmail(user.email)]?.today || 0}
                      </td>
                      <td style={{ padding: '0.75rem 0.5rem', textAlign: 'center', fontWeight: 600 }}>
                        {userUsageData[encodeEmail(user.email)]?.total || 0}
                      </td>
                      <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={!!user.allow}
                          disabled={updatingUserId === user.email}
                          onChange={async () => {
                            setUpdatingUserId(user.email);
                            await setAllow(user, !user.allow);
                            setUpdatingUserId(null);
                          }}
                          style={{ width: '1.2em', height: '1.2em', cursor: updatingUserId === user.email ? 'not-allowed' : 'pointer' }}
                        />
                      </td>
                      <td style={{ padding: '0.75rem', textAlign: 'center' }}>
                        <input
                          type="checkbox"
                          checked={!!user.admin}
                          disabled={updatingUserId === user.email}
                          onChange={async () => {
                            setUpdatingUserId(user.email);
                            await setAdmin(user, !user.admin);
                            setUpdatingUserId(null);
                          }}
                          style={{ width: '1.2em', height: '1.2em', cursor: updatingUserId === user.email ? 'not-allowed' : 'pointer' }}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Add User Modal */}
      {showAddUser && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '2rem',
            width: '400px',
            maxWidth: '90vw',
            boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
          }}>
            <h3 style={{ color: '#667eea', fontWeight: 700, fontSize: '1.25rem', marginBottom: '1.5rem' }}>Add New User</h3>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#333' }}>Email</label>
              <input
                type="email"
                placeholder="Enter email address"
                value={newUser.email}
                onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                className="form-control"
              />
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#333' }}>Name</label>
              <input
                type="text"
                placeholder="Enter full name"
                value={newUser.name}
                onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                className="form-control"
              />
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#333' }}>Password</label>
              <input
                type="password"
                placeholder="Enter password (min 6 characters)"
                value={newUser.password}
                onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                className="form-control"
              />
            </div>

            <div style={{ display: 'flex', gap: '1rem' }}>
              <button
                className="btn btn-primary"
                onClick={createNewUser}
                disabled={creatingUser}
                style={{ flex: 1 }}
              >
                {creatingUser ? 'Creating...' : 'Create User'}
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => { setShowAddUser(false); setNewUser({ email: '', name: '', password: '' }); }}
                style={{ flex: 1 }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit User Modal */}
      {showEditUser && editingUser && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '2rem',
            width: '400px',
            maxWidth: '90vw',
            boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
          }}>
            <h3 style={{ color: '#667eea', fontWeight: 700, fontSize: '1.25rem', marginBottom: '1.5rem' }}>Edit User</h3>
            <p style={{ color: '#666', marginBottom: '1.5rem', fontSize: '0.9rem' }}>Editing: <strong>{editingUser.email}</strong></p>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#333' }}>Name</label>
              <input
                type="text"
                placeholder="Enter full name"
                value={editUser.name}
                onChange={(e) => setEditUser({ ...editUser, name: e.target.value })}
                className="form-control"
              />
            </div>

            <div style={{ marginBottom: '1.5rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#333' }}>New Password (optional)</label>
              <input
                type="password"
                placeholder="Leave blank to keep current password"
                value={editUser.password}
                onChange={(e) => setEditUser({ ...editUser, password: e.target.value })}
                className="form-control"
              />
            </div>

            <div style={{ display: 'flex', gap: '1rem' }}>
              <button
                className="btn btn-primary"
                onClick={updateUserInfo}
                disabled={updatingUser}
                style={{ flex: 1 }}
              >
                {updatingUser ? 'Updating...' : 'Update User'}
              </button>
              <button
                className="btn btn-secondary"
                onClick={() => { setShowEditUser(false); setEditingUser(null); setEditUser({ name: '', password: '' }); }}
                style={{ flex: 1 }}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
} 